// Block 3.6 «Нетарифные документы» — рендер 31-колоночной матрицы
// S11 resolver result, плюс ручной upload документов BLOCKED-типов
// (vet / phyto / license_minpromtorg / license_other).
//
// API:
//   GET  /api/v1/deals/:dealId/client-documents
//   POST /api/v1/deals/:dealId/client-documents (multipart)
//   GET  /api/v1/client-documents/:id/presigned
//
// S11 ResultDocument endpoint:
//   GET  /api/v1/deals/:dealId/s11-result
// Если endpoint вернул 404 / 503 — section показывает заглушку:
// для каждого approved-кандидата строим строку с kind='unknown' для всех
// 7 doc-cells. Брокер всё равно может загружать client_documents.
//
// © GSCG 2026 · UI block 3.6
import { useEffect, useMemo, useState } from 'react';

export type CellKind = 'valid' | 'required_missing' | 'not_required' | 'unknown';

export interface Cell {
  display: string;
  href?: string;
  kind: CellKind;
  confidence_score?: 'tnved_only' | 'tnved+desc' | 'tnved+desc+article' | 'full_match';
}

export interface S11Row {
  id: string;
  code: string;
  description: string;
  tech_reg: string;
  fsa_decl: Cell;
  fsa_decl_holder: string;
  fsa_decl_manufacturer: string;
  fsa_cert: Cell;
  fsa_cert_holder: string;
  fsa_cert_manufacturer: string;
  sgr: Cell;
  sgr_holder: string;
  sgr_manufacturer: string;
  vet: Cell;
  vet_holder: Cell;
  vet_manufacturer: Cell;
  phyto: Cell;
  phyto_holder: Cell;
  fsb: Cell;
  fsb_holder: Cell;
  fsb_manufacturer: string;
  license: Cell;
  license_holder: string;
  attr_article: string;
  attr_model: string;
  attr_brand: string;
  attr_manufacturer: string;
  attr_country: string;
  consistent: 'ok' | 'mismatch' | 'unknown';
  download_row: string;
  row_status: 'ok' | 'partial' | 'not_found' | 'error';
  candidate_id?: string;
}

export interface ClientDocument {
  id: string;
  deal_id: string;
  candidate_id: string | null;
  document_type: 'vet' | 'phyto' | 'license_minpromtorg' | 'license_other';
  document_number: string;
  applicant_name: string | null;
  manufacturer_name: string | null;
  product_description: string | null;
  tnved_codes: string[];
  issue_date: string | null;
  expiry_date: string | null;
  scan_url: string | null;
  uploaded_at: string;
  notes: string | null;
}

export interface CandidateLite {
  id: string;
  tnved_code: string | null;
  approved_code: string | null;
  status: string;
  name_ru: string | null;
  name: string | null;
}

// Какой DocumentType покрывает данную ячейку (kind=required_missing).
// Возвращает null если ячейка не покрывается ручной загрузкой
// (FSA cert/decl/SGR/FSB не загружаются вручную — они автоматические).
function cellToClientDocType(
  colKey: keyof S11Row,
): 'vet' | 'phyto' | 'license_minpromtorg' | 'license_other' | null {
  if (colKey === 'vet') return 'vet';
  if (colKey === 'phyto') return 'phyto';
  if (colKey === 'license') return 'license_minpromtorg';
  return null;
}

const KIND_BADGE: Record<CellKind, string> = {
  valid: 'bg-emerald-100 text-emerald-800 ring-emerald-300',
  required_missing: 'bg-red-100 text-red-800 ring-red-300',
  not_required: 'bg-gray-100 text-gray-500 ring-gray-200',
  unknown: 'bg-yellow-50 text-yellow-800 ring-yellow-200',
};

interface CellViewProps {
  cell: Cell;
  onUpload?: () => void;
  uploadEnabled?: boolean;
}

function CellView({ cell, onUpload, uploadEnabled }: CellViewProps): JSX.Element {
  if (cell.kind === 'valid') {
    return (
      <div className="space-y-0.5">
        {cell.href ? (
          <a
            href={cell.href}
            target="_blank"
            rel="noreferrer"
            className="text-blue-700 underline hover:text-blue-900"
          >
            {cell.display}
          </a>
        ) : (
          <span className="text-emerald-800">{cell.display}</span>
        )}
        {cell.confidence_score && (
          <div className="text-[10px] uppercase tracking-wide text-emerald-700">
            {cell.confidence_score.replace('+', '·')}
          </div>
        )}
      </div>
    );
  }
  if (cell.kind === 'required_missing') {
    return (
      <div className="space-y-1">
        <span
          className={
            'inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ' +
            KIND_BADGE.required_missing
          }
        >
          требуется, не найдено
        </span>
        {uploadEnabled && onUpload && (
          <button
            type="button"
            onClick={onUpload}
            className="block w-full rounded border border-dashed border-blue-400 bg-blue-50 px-1.5 py-0.5 text-[11px] text-blue-700 hover:bg-blue-100"
          >
            📎 Загрузить документ
          </button>
        )}
      </div>
    );
  }
  if (cell.kind === 'not_required') {
    return <span className="text-gray-400">—</span>;
  }
  return (
    <span
      className={
        'inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ' +
        KIND_BADGE.unknown
      }
    >
      нет данных
    </span>
  );
}

// 31 column definitions (порядок и keys — синхронны с S11_RESULT_COLUMNS).
interface ColumnDef {
  key: keyof S11Row;
  label: string;
  group: 'id' | 'fsa_decl' | 'fsa_cert' | 'sgr' | 'vet' | 'phyto' | 'fsb' | 'license' | 'attr' | 'meta';
  width?: string;
}

const COLUMNS: ColumnDef[] = [
  { key: 'id', label: '#', group: 'id', width: 'w-12' },
  { key: 'code', label: 'ТН ВЭД', group: 'id', width: 'w-28' },
  { key: 'description', label: 'Описание', group: 'id' },
  { key: 'tech_reg', label: 'ТР ТС', group: 'id', width: 'w-28' },
  { key: 'fsa_decl', label: 'ФСА декл.', group: 'fsa_decl' },
  { key: 'fsa_decl_holder', label: 'Заявитель', group: 'fsa_decl' },
  { key: 'fsa_decl_manufacturer', label: 'Производитель', group: 'fsa_decl' },
  { key: 'fsa_cert', label: 'ФСА серт.', group: 'fsa_cert' },
  { key: 'fsa_cert_holder', label: 'Заявитель', group: 'fsa_cert' },
  { key: 'fsa_cert_manufacturer', label: 'Производитель', group: 'fsa_cert' },
  { key: 'sgr', label: 'СГР', group: 'sgr' },
  { key: 'sgr_holder', label: 'Заявитель', group: 'sgr' },
  { key: 'sgr_manufacturer', label: 'Производитель', group: 'sgr' },
  { key: 'vet', label: 'ВетИС', group: 'vet' },
  { key: 'vet_holder', label: 'Заявитель', group: 'vet' },
  { key: 'vet_manufacturer', label: 'Производитель', group: 'vet' },
  { key: 'phyto', label: 'Фито', group: 'phyto' },
  { key: 'phyto_holder', label: 'Заявитель', group: 'phyto' },
  { key: 'fsb', label: 'ФСБ', group: 'fsb' },
  { key: 'fsb_holder', label: 'Заявитель', group: 'fsb' },
  { key: 'fsb_manufacturer', label: 'Производитель', group: 'fsb' },
  { key: 'license', label: 'Лицензия', group: 'license' },
  { key: 'license_holder', label: 'Лицензиат', group: 'license' },
  { key: 'attr_article', label: 'Артикул', group: 'attr' },
  { key: 'attr_model', label: 'Модель', group: 'attr' },
  { key: 'attr_brand', label: 'Бренд', group: 'attr' },
  { key: 'attr_manufacturer', label: 'Производитель', group: 'attr' },
  { key: 'attr_country', label: 'Страна', group: 'attr' },
  { key: 'consistent', label: 'Согласованность', group: 'meta' },
  { key: 'download_row', label: 'Скачать', group: 'meta' },
  { key: 'row_status', label: 'Статус', group: 'meta' },
];

function isCell(v: unknown): v is Cell {
  return typeof v === 'object' && v !== null && 'kind' in v;
}

interface UploadModalProps {
  dealId: string;
  candidate: CandidateLite | null;
  initialType: 'vet' | 'phyto' | 'license_minpromtorg' | 'license_other';
  token: string;
  onClose: () => void;
  onSuccess: (doc: ClientDocument) => void;
}

function UploadModal(props: UploadModalProps): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [docType, setDocType] = useState<UploadModalProps['initialType']>(props.initialType);
  const [docNumber, setDocNumber] = useState('');
  const [applicant, setApplicant] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [productDesc, setProductDesc] = useState('');
  const [tnvedRaw, setTnvedRaw] = useState(
    props.candidate?.approved_code ?? props.candidate?.tnved_code ?? '',
  );
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setErr(null);
    if (!file) {
      setErr('Выберите файл (PDF / JPG / PNG)');
      return;
    }
    if (!docNumber.trim()) {
      setErr('Укажите номер документа');
      return;
    }
    setBusy(true);
    try {
      const tnvedCodes = tnvedRaw
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter((s) => /^\d{4,10}$/.test(s));
      const metadata: Record<string, unknown> = {
        document_type: docType,
        document_number: docNumber.trim(),
        applicant_name: applicant.trim() || undefined,
        manufacturer_name: manufacturer.trim() || undefined,
        product_description: productDesc.trim() || undefined,
        tnved_codes: tnvedCodes,
        issue_date: issueDate || undefined,
        expiry_date: expiryDate || undefined,
        candidate_id: props.candidate?.id,
        notes: notes.trim() || undefined,
      };
      const fd = new FormData();
      fd.append('metadata', JSON.stringify(metadata));
      fd.append('file', file);

      const r = await fetch(`/api/v1/deals/${props.dealId}/client-documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${props.token}` },
        body: fd,
      });
      const j = (await r.json()) as ClientDocument & { error?: string; message?: string };
      if (!r.ok) {
        setErr(j.message ?? j.error ?? `HTTP ${r.status}`);
      } else {
        props.onSuccess(j);
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={props.onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-ntr-primary">
              📎 Загрузка документа от клиента
            </h3>
            <p className="text-xs text-ntr-muted">
              Скан + метаданные → S3, привязка к {props.candidate?.id ? 'строке инвойса' : 'сделке'}
            </p>
          </div>
          <button
            type="button"
            onClick={props.onClose}
            className="rounded p-1 text-gray-400 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <form onSubmit={submit} className="mt-4 space-y-3 text-sm">
          <div>
            <label className="mb-1 block text-xs text-ntr-muted">Тип документа *</label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value as typeof docType)}
              className="w-full rounded border border-gray-300 px-2 py-1"
            >
              <option value="vet">ВетИС (Меркурий)</option>
              <option value="phyto">Фитосанитарный (Аргус)</option>
              <option value="license_minpromtorg">Лицензия Минпромторга</option>
              <option value="license_other">Прочая лицензия</option>
            </select>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-ntr-muted">Номер документа *</label>
              <input
                value={docNumber}
                onChange={(e) => setDocNumber(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1 font-mono"
                placeholder="RU 77.99.32.001..."
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-ntr-muted">ТН ВЭД (через запятую)</label>
              <input
                value={tnvedRaw}
                onChange={(e) => setTnvedRaw(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1 font-mono"
                placeholder="2106909809"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-ntr-muted">Заявитель</label>
              <input
                value={applicant}
                onChange={(e) => setApplicant(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-ntr-muted">Производитель</label>
              <input
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-ntr-muted">Дата выдачи</label>
              <input
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-ntr-muted">Срок действия *</label>
              <input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs text-ntr-muted">Описание товара</label>
            <input
              value={productDesc}
              onChange={(e) => setProductDesc(e.target.value)}
              className="w-full rounded border border-gray-300 px-2 py-1"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs text-ntr-muted">Заметки</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded border border-gray-300 px-2 py-1"
              rows={2}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs text-ntr-muted">Файл скана *</label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="w-full text-xs"
              required
            />
            <p className="mt-1 text-xs text-ntr-muted">
              PDF / JPG / PNG до 25 МБ. Уйдёт в DO Spaces (gscg-customs-uploads).
            </p>
          </div>

          {err && (
            <div className="rounded bg-red-50 px-2 py-1 text-xs text-red-700">{err}</div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={props.onClose}
              className="rounded border border-gray-300 px-4 py-1 text-sm text-gray-700 hover:bg-gray-50"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={busy}
              className="rounded bg-ntr-primary px-4 py-1 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {busy ? 'Загрузка…' : 'Загрузить и привязать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DealNonTariffSectionProps {
  dealId: string;
  token: string;
  candidates: CandidateLite[];
}

export function DealNonTariffSection(props: DealNonTariffSectionProps): JSX.Element {
  const [rows, setRows] = useState<S11Row[]>([]);
  const [clientDocs, setClientDocs] = useState<ClientDocument[]>([]);
  const [s11Available, setS11Available] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [modal, setModal] = useState<
    | {
        candidate: CandidateLite | null;
        initialType: 'vet' | 'phyto' | 'license_minpromtorg' | 'license_other';
      }
    | null
  >(null);
  const [reloadTick, setReloadTick] = useState(0);

  // Fetch S11 result + client_documents in parallel.
  useEffect(() => {
    void (async () => {
      setBusy(true);
      setErr(null);
      try {
        const [rS11, rCD] = await Promise.all([
          fetch(`/api/v1/deals/${props.dealId}/s11-result`, {
            headers: { Authorization: `Bearer ${props.token}` },
          }).catch(() => null),
          fetch(`/api/v1/deals/${props.dealId}/client-documents`, {
            headers: { Authorization: `Bearer ${props.token}` },
          }),
        ]);

        if (rS11 && rS11.ok) {
          const data = (await rS11.json()) as { rows?: S11Row[] };
          setRows(data.rows ?? []);
          setS11Available(true);
        } else {
          // Fallback: synthesize rows from approved candidates with kind=unknown.
          setS11Available(false);
          setRows(synthRowsFromCandidates(props.candidates));
        }

        if (rCD.ok) {
          const data = (await rCD.json()) as { client_documents?: ClientDocument[] };
          setClientDocs(data.client_documents ?? []);
        }
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setBusy(false);
      }
    })();
  }, [props.dealId, props.token, reloadTick, props.candidates]);

  // Apply client_documents overlay: для каждой ячейки kind=required_missing,
  // если есть client_document того же типа и привязка по candidate_id ИЛИ
  // tnved_codes пересекается с rows[i].code — перекрываем на 'valid'.
  const effectiveRows = useMemo(() => overlayClientDocs(rows, clientDocs), [rows, clientDocs]);

  const summary = useMemo(() => summarize(effectiveRows), [effectiveRows]);

  const onUploadFor = (row: S11Row, colKey: keyof S11Row): void => {
    const dt = cellToClientDocType(colKey);
    if (!dt) return;
    const candidate = props.candidates.find((c) => c.id === row.candidate_id) ?? null;
    setModal({ candidate, initialType: dt });
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-ntr-primary/10">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-ntr-primary">
          3.6. Нетарифные документы
        </h3>
        <div className="flex items-center gap-2 text-sm">
          {summary.requiredMissing === 0 ? (
            <span className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-emerald-800 ring-1 ring-emerald-300">
              ✓ Все ОК
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-red-100 px-3 py-1 text-red-800 ring-1 ring-red-300">
              ⚠ Нужны {summary.requiredMissing} документов
            </span>
          )}
          <button
            type="button"
            onClick={() => setReloadTick((t) => t + 1)}
            className="text-xs text-ntr-primary hover:underline"
          >
            ↻ Обновить
          </button>
        </div>
      </div>

      {s11Available === false && (
        <p className="mt-2 text-xs text-amber-700">
          S11 resolver временно недоступен — показаны строки из утверждённых кодов ТН ВЭД;
          ячейки имеют статус «нет данных». Документы клиента (vet/phyto/license) можно
          загружать прямо сейчас, они перекроют ячейки автоматически.
        </p>
      )}

      {err && <div className="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      {busy && <p className="mt-3 text-sm text-ntr-muted">Загрузка…</p>}

      {effectiveRows.length === 0 && !busy ? (
        <p className="mt-3 text-sm text-ntr-muted">
          Нет утверждённых строк инвойса. Подтвердите ТН ВЭД в блоке 3.3 — таблица заполнится.
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-0 text-xs">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key as string}
                    className={
                      'whitespace-nowrap border-b border-gray-200 px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-ntr-muted ' +
                      (c.width ?? '')
                    }
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {effectiveRows.map((row, idx) => (
                <tr key={row.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                  {COLUMNS.map((col) => {
                    const v = row[col.key];
                    return (
                      <td
                        key={col.key as string}
                        className="whitespace-nowrap border-b border-gray-100 px-2 py-2 align-top"
                      >
                        {renderCell(v, col.key, () => onUploadFor(row, col.key))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {clientDocs.length > 0 && (
        <details className="mt-4 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
          <summary className="cursor-pointer font-medium text-ntr-primary">
            📎 Загруженные документы клиента ({clientDocs.length})
          </summary>
          <ul className="mt-2 divide-y divide-gray-200">
            {clientDocs.map((d) => (
              <li key={d.id} className="flex items-center justify-between py-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-blue-800">
                      {d.document_type}
                    </span>
                    <span className="font-mono text-xs">{d.document_number}</span>
                  </div>
                  <p className="text-[11px] text-ntr-muted">
                    {d.applicant_name ?? '—'} · ТН ВЭД: {d.tnved_codes.join(', ') || '—'} ·
                    срок: {d.expiry_date ?? '—'}
                  </p>
                </div>
                <DownloadButton docId={d.id} token={props.token} />
              </li>
            ))}
          </ul>
        </details>
      )}

      {modal && (
        <UploadModal
          dealId={props.dealId}
          candidate={modal.candidate}
          initialType={modal.initialType}
          token={props.token}
          onClose={() => setModal(null)}
          onSuccess={(newDoc) => {
            setClientDocs((prev) => [newDoc, ...prev]);
            setModal(null);
          }}
        />
      )}
    </div>
  );
}

function DownloadButton({ docId, token }: { docId: string; token: string }): JSX.Element {
  const open = async (): Promise<void> => {
    const r = await fetch(`/api/v1/client-documents/${docId}/presigned`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) {
      const data = (await r.json()) as { url: string };
      window.open(data.url, '_blank');
    }
  };
  return (
    <button
      type="button"
      onClick={() => void open()}
      className="text-xs text-blue-700 underline hover:text-blue-900"
    >
      Скачать
    </button>
  );
}

function renderCell(
  v: unknown,
  colKey: keyof S11Row,
  onUpload: () => void,
): JSX.Element {
  if (isCell(v)) {
    const dt = cellToClientDocType(colKey);
    return <CellView cell={v} onUpload={onUpload} uploadEnabled={dt !== null} />;
  }
  if (colKey === 'consistent') {
    const tone =
      v === 'ok'
        ? 'bg-emerald-100 text-emerald-800'
        : v === 'mismatch'
        ? 'bg-red-100 text-red-800'
        : 'bg-gray-100 text-gray-700';
    return (
      <span className={'inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium ' + tone}>
        {String(v ?? '—')}
      </span>
    );
  }
  if (colKey === 'row_status') {
    const tone =
      v === 'ok'
        ? 'bg-emerald-100 text-emerald-800'
        : v === 'partial'
        ? 'bg-yellow-100 text-yellow-800'
        : v === 'not_found'
        ? 'bg-red-100 text-red-800'
        : 'bg-gray-100 text-gray-700';
    return (
      <span className={'inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium ' + tone}>
        {String(v ?? '—')}
      </span>
    );
  }
  if (colKey === 'download_row') {
    if (typeof v === 'string' && v.startsWith('http')) {
      return (
        <a href={v} target="_blank" rel="noreferrer" className="text-blue-700 underline">
          ZIP
        </a>
      );
    }
    return <span className="text-gray-400">—</span>;
  }
  return <span>{v == null || v === '' ? '—' : String(v)}</span>;
}

function summarize(rows: S11Row[]): { requiredMissing: number; total: number } {
  let requiredMissing = 0;
  for (const r of rows) {
    for (const c of COLUMNS) {
      const v = r[c.key];
      if (isCell(v) && v.kind === 'required_missing') requiredMissing += 1;
    }
  }
  return { requiredMissing, total: rows.length };
}

function synthRowsFromCandidates(cands: CandidateLite[]): S11Row[] {
  const eligible = cands.filter((c) => c.status === 'approved' || c.status === 'overridden');
  return eligible.map<S11Row>((c, i) => ({
    id: `row-${i + 1}`,
    code: c.approved_code ?? c.tnved_code ?? '',
    description: c.name_ru ?? c.name ?? '',
    tech_reg: '',
    fsa_decl: { display: 'нет данных', kind: 'unknown' },
    fsa_decl_holder: '',
    fsa_decl_manufacturer: '',
    fsa_cert: { display: 'нет данных', kind: 'unknown' },
    fsa_cert_holder: '',
    fsa_cert_manufacturer: '',
    sgr: { display: 'нет данных', kind: 'unknown' },
    sgr_holder: '',
    sgr_manufacturer: '',
    vet: { display: 'нет данных', kind: 'unknown' },
    vet_holder: { display: '—', kind: 'not_required' },
    vet_manufacturer: { display: '—', kind: 'not_required' },
    phyto: { display: 'нет данных', kind: 'unknown' },
    phyto_holder: { display: '—', kind: 'not_required' },
    fsb: { display: 'нет данных', kind: 'unknown' },
    fsb_holder: { display: '—', kind: 'not_required' },
    fsb_manufacturer: '',
    license: { display: 'нет данных', kind: 'unknown' },
    license_holder: '',
    attr_article: '',
    attr_model: '',
    attr_brand: '',
    attr_manufacturer: '',
    attr_country: '',
    consistent: 'unknown',
    download_row: '',
    row_status: 'not_found',
    candidate_id: c.id,
  }));
}

function overlayClientDocs(rows: S11Row[], docs: ClientDocument[]): S11Row[] {
  if (rows.length === 0 || docs.length === 0) return rows;
  return rows.map((row) => {
    const matching = docs.filter((d) => {
      if (d.candidate_id && d.candidate_id === row.candidate_id) return true;
      if (d.tnved_codes && d.tnved_codes.some((t) => row.code.startsWith(t) || t.startsWith(row.code))) {
        return true;
      }
      return false;
    });
    if (matching.length === 0) return row;
    const next: S11Row = { ...row };
    for (const d of matching) {
      const cell: Cell = {
        display: d.document_number,
        href: d.scan_url
          ? `/api/v1/client-documents/${d.id}/presigned`
          : undefined,
        kind: 'valid',
      };
      if (d.document_type === 'vet') next.vet = cell;
      else if (d.document_type === 'phyto') next.phyto = cell;
      else if (d.document_type === 'license_minpromtorg' || d.document_type === 'license_other') {
        next.license = cell;
        if (d.applicant_name) next.license_holder = d.applicant_name;
      }
    }
    return next;
  });
}
